// Danh sách việc cần xử lý (cuộc nhỡ, khách mới, hẹn gọi lại, xác nhận lịch, khách không đến, bỏ quên)
import React, { useMemo, useState } from 'react';
import { Button, Segmented, Select, Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Check } from 'lucide-react';
import { AGENTS } from './data';
import { TASK_META, isOverdue, leadStage, type Ctx, type Task, type TaskKind } from './logic';
import useTeleDash from './store';
import { AgentName, DueText, StageChip } from './ui';

const KINDS: TaskKind[] = ['missed', 'new', 'callback', 'confirm', 'noshow', 'forgot'];

export default function TaskTable({ ctx, list, manage, kind, onKind, height = 420 }: {
  ctx: Ctx; list: Task[]; manage?: boolean; kind: TaskKind | 'all'; onKind: (k: TaskKind | 'all') => void; height?: number;
}) {
  const { markDone, undoDone, reassign, patchAppt } = useTeleDash();
  const [onlyLate, setOnlyLate] = useState(false);
  const counts = useMemo(() => Object.fromEntries(KINDS.map(k => [k, list.filter(t => t.kind === k).length])) as Record<TaskKind, number>, [list]);
  const shown = list.filter(t => (kind === 'all' || t.kind === kind) && (!onlyLate || isOverdue(t, ctx.now)));
  const late = list.filter(t => isOverdue(t, ctx.now)).length;

  const finish = (t: Task) => {
    if (t.kind === 'confirm' && t.apptId) patchAppt(t.apptId, { status: 'confirmed' });
    markDone(t.key);
    message.success({ content: <span>{t.kind === 'confirm' ? 'Đã xác nhận lịch' : 'Đã xử lý'} · {t.leadId ?? t.phone} <Button type="link" size="small" onClick={() => { undoDone(t.key); if (t.kind === 'confirm' && t.apptId) patchAppt(t.apptId, { status: undefined }); }}>Hoàn tác</Button></span> });
  };

  const columns: ColumnsType<Task> = [
    {
      title: 'Việc', key: 'kind',
      render: (_, t) => (
        <div className="max-w-[230px] leading-tight">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-medium text-gray-800 dark:text-gray-100">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: TASK_META[t.kind].color }} />{TASK_META[t.kind].label}
          </span>
          <div className="mt-0.5 text-[11px] text-gray-500">{t.detail}</div>
        </div>
      ),
    },
    {
      title: 'Khách', key: 'who',
      render: (_, t) => {
        const l = t.leadId ? ctx.m.leadById[t.leadId] : undefined;
        return l ? (
          <div className="leading-tight">
            <div className="flex items-center gap-1.5 whitespace-nowrap"><b className="text-gray-800 dark:text-gray-100">{l.name}</b><StageChip s={leadStage(l, ctx)} /></div>
            <div className="text-[11px] text-gray-500">{l.id} · {l.phone} · {l.mang}</div>
          </div>
        ) : <div className="leading-tight"><b className="text-gray-800 dark:text-gray-100">Số lạ</b><div className="text-[11px] text-gray-500">{t.phone}</div></div>;
      },
    },
    ...(manage ? [{
      title: 'Phụ trách', key: 'agent', width: 170,
      render: (_: unknown, t: Task) => (
        <Select size="small" variant="borderless" className="w-full" value={t.agentId} popupMatchSelectWidth={220}
          onChange={v => { reassign(t.key, v); message.success(`Đã giao lại cho ${AGENTS.find(a => a.id === v)?.name}`); }}
          options={AGENTS.map(a => ({ value: a.id, label: <AgentName id={a.id} /> }))} labelRender={({ value }) => <AgentName id={String(value)} compact />} />
      ),
    }] : []),
    { title: 'Hạn', key: 'due', width: 110, sorter: (a, b) => a.due - b.due, render: (_, t) => <DueText due={t.due} now={ctx.now} /> },
    {
      title: '', key: 'act', width: manage ? 48 : 120, align: 'right',
      render: (_, t) => (
        <Tooltip title={t.kind === 'confirm' ? 'Khách đã xác nhận đến — cập nhật lịch sang Đã xác nhận' : 'Đánh dấu đã gọi / đã xử lý (tổng đài sẽ tự ghi nhận khi tích hợp)'}>
          <Button size="small" icon={<Check size={13} />} onClick={() => finish(t)}>{manage ? null : t.kind === 'confirm' ? 'Đã xác nhận' : 'Đã xử lý'}</Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented size="small" value={kind} onChange={v => onKind(v as TaskKind | 'all')}
          options={[{ value: 'all', label: `Tất cả (${list.length})` }, ...KINDS.filter(k => counts[k]).map(k => ({ value: k, label: `${TASK_META[k].short} (${counts[k]})` }))]} />
        <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" className="accent-[#3e91ab]" checked={onlyLate} onChange={e => setOnlyLate(e.target.checked)} />
          Chỉ việc trễ hạn <b className={late ? 'text-red-500' : ''}>({late})</b>
        </label>
      </div>
      <Table<Task> size="small" rowKey="key" columns={columns} dataSource={shown} pagination={shown.length > 50 ? { pageSize: 50, size: 'small', showSizeChanger: false } : false}
        scroll={{ x: 'max-content', y: height }} locale={{ emptyText: 'Không còn việc nào — tốt lắm!' }}
        rowClassName={t => (isOverdue(t, ctx.now) ? 'bg-red-50/60 dark:bg-red-500/5' : '')} />
    </div>
  );
}
